import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Flex,
  FormControl,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Image,
  HStack,
  Divider,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { Rounds, EventTypes, JeopardyStates } from "types";
import { useParams } from "react-router-dom";
import { Player } from "../Player/Player";
import { HostOnlyControls } from "./HostOnlyControls";
import { PlayerManagement } from "./PlayerManagement";
import { useChromecast } from "../../hooks/useChromecast";
import { motion } from "framer-motion";
import {
  FaChromecast,
  FaShareAlt,
  FaQrcode,
  FaTv,
  FaPlay,
  FaUsers,
  FaForward,
  FaFastForward,
  FaCog,
} from "react-icons/fa";

const qrCodeBaseUrl =
  "https://api.qrserver.com/v1/create-qr-code/?size=200x200";

type HostPhase = "setup" | "joining" | "playing";


export const HostGame: React.FC = () => {
  const { gameId: urlGameId } = useParams<{ gameId: string }>();
  const {
    gameState,
    currentPlayer,
    setGameId,
    addPlayer,
    isLoadingGameState,
    sendEvent,
    isHost,
    isHostOnly,
    setIsHost,
    setHostOnly,
  } = useGame();
  const toast = useToast();
  const {
    isOpen: isShareOpen,
    onOpen: onShareOpen,
    onClose: onShareClose,
  } = useDisclosure();
  const {
    isOpen: isTvOpen,
    onOpen: onTvOpen,
    onClose: onTvClose,
  } = useDisclosure();
  const {
    isOpen: isPlayersOpen,
    onOpen: onPlayersOpen,
    onClose: onPlayersClose,
  } = useDisclosure();
  const { requestSession, endSession, isAvailable, isConnected } =
    useChromecast();

  const [hostPhase, setHostPhase] = useState<HostPhase>("setup");
  const [hostName, setHostName] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [displayOpened, setDisplayOpened] = useState(false);

  // Detect iOS - check immediately (safe because this only runs in browser)
  const isIOS = typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );

  const castReceiverUrl = `${window.location.origin}/cast/${urlGameId}`;
  const playerJoinUrl = `${window.location.origin}/play/${urlGameId}`;
  const qrCodeUrl = `${qrCodeBaseUrl}&data=${encodeURIComponent(
    playerJoinUrl
  )}`;

  // Set game ID from URL
  useEffect(() => {
    if (urlGameId) {
      setGameId(urlGameId);
    }
  }, [urlGameId, setGameId]);

  // Check if we're already a player or host-only (returning to game)
  useEffect(() => {
    if (gameState && (currentPlayer || isHostOnly)) {
      // We're on /host route, so mark as host
      if (!isHost) {
        setIsHost(true);
      }
      setHostPhase("playing");
    }
  }, [gameState, currentPlayer, isHostOnly, isHost, setIsHost]);

  const handleJoinAsPlayer = async () => {
    if (!urlGameId || !hostName.trim()) return;

    setIsJoining(true);
    setHostPhase("joining");
    try {
      await addPlayer({ name: hostName.trim(), gameId: urlGameId });
      setIsHost(true);
      setHostPhase("playing");
    } catch (e) {
      console.error("Failed to join as host player:", e);
      setHostPhase("setup");
      toast({
        title: "Failed to join",
        description: "Could not join the game. Try a different name.",
        status: "error",
        duration: 3000,
      });
    }
    setIsJoining(false);
  };

  const handleCastClick = async () => {
    if (isConnected) {
      endSession();
      toast({
        title: "Disconnected from Chromecast",
        status: "info",
        duration: 2000,
      });
    } else if (isAvailable) {
      const success = await requestSession();
      if (success) {
        setDisplayOpened(true);
        toast({
          title: "Connected!",
          description: "Game board is now on your TV",
          status: "success",
          duration: 3000,
        });
      }
    } else {
      // Fallback: open game board in new window
      window.open(castReceiverUrl, "_blank");
      setDisplayOpened(true);
      toast({
        title: "Game board opened",
        description: "Display this on your TV",
        status: "info",
        duration: 3000,
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my Jeoparty game!",
          text: `Join my Jeoparty game with code: ${urlGameId?.toUpperCase()}`,
          url: playerJoinUrl,
        });
      } catch (e) {
        // User cancelled or share failed
        onShareOpen();
      }
    } else {
      onShareOpen();
    }
  };

  const copyGameCode = () => {
    navigator.clipboard.writeText(urlGameId?.toUpperCase() || "");
    toast({
      title: "Game code copied!",
      status: "success",
      duration: 2000,
    });
  };

  const copyJoinLink = () => {
    navigator.clipboard.writeText(playerJoinUrl);
    toast({
      title: "Join link copied!",
      status: "success",
      duration: 2000,
    });
  };

  if (isLoadingGameState) {
    return (
      <Center height="100%" width="100%" background="jeopardy.blue">
        <VStack spacing={4}>
          <Spinner size="xl" color="white" />
          <Text textStyle="clue">Loading game...</Text>
        </VStack>
      </Center>
    );
  }

  if (!gameState) {
    return (
      <Center height="100%" width="100%" background="jeopardy.blue">
        <VStack spacing={4}>
          <Text textStyle="clue" fontSize="2xl">
            Game not found
          </Text>
          <Text textStyle="clue" opacity={0.7}>
            This game may still be generating or doesn't exist.
          </Text>
          <Button
            onClick={() => window.location.reload()}
            bg="jeopardy.lightBlue"
          >
            <Text textStyle="clue">Retry</Text>
          </Button>
        </VStack>
      </Center>
    );
  }

  // Setup Phase - Cast/Display setup before joining as player
  if (hostPhase === "setup") {
    return (
      <Flex
        height="100%"
        width="100%"
        background="jeopardy.blue"
        flexDir="column"
      >
        {/* Top Bar with Cast Icon */}
        <Flex
          width="100%"
          py={3}
          px={4}
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          {/* iOS: Direct link to CastHelper via custom URL scheme */}
          {isIOS ? (
            <IconButton
              as="a"
              href={`casthelper://cast?code=${urlGameId}&app=82ADCE54&name=Jeoparty&receiver=${encodeURIComponent(castReceiverUrl)}&return=${encodeURIComponent(playerJoinUrl)}`}
              aria-label="Cast to TV"
              icon={<FaChromecast size={24} />}
              size="lg"
              bg="transparent"
              color="white"
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
            />
          ) : (
            <IconButton
              aria-label={isConnected ? "Disconnect Chromecast" : "Cast to TV"}
              icon={
                <Box position="relative">
                  <FaChromecast size={24} />
                  {isConnected && (
                    <Box
                      position="absolute"
                      bottom={-1}
                      right={-1}
                      width={3}
                      height={3}
                      bg="green.400"
                      borderRadius="full"
                      border="2px solid"
                      borderColor="jeopardy.blue"
                    />
                  )}
                </Box>
              }
              size="lg"
              onClick={() => requestSession(urlGameId)}
              bg={isConnected ? "green.500" : "transparent"}
              color="white"
              _hover={{
                bg: isConnected ? "green.400" : "rgba(255,255,255,0.1)",
              }}
            />
          )}
        </Flex>

        {/* Main Content */}
        <Center flex={1} p={4} overflow="auto">
          <VStack spacing={6} width="100%" maxWidth="400px">
            {/* Header */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Text fontSize="2.5em" textStyle="jeoparty" textAlign="center">
                JEOPARTY
              </Text>
            </motion.div>

            {/* Game Code Display */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <VStack spacing={3} width="100%">
                <Text textStyle="clue" fontSize="sm" opacity={0.8}>
                  Game Code
                </Text>
                <Box
                  bg="jeopardy.gold"
                  px={6}
                  py={3}
                  borderRadius="lg"
                  cursor="pointer"
                  onClick={copyGameCode}
                  _hover={{ transform: "scale(1.02)" }}
                  transition="transform 0.2s"
                >
                  <Text
                    fontSize="2.5em"
                    fontWeight="bold"
                    color="black"
                    letterSpacing="0.15em"
                    textAlign="center"
                  >
                    {urlGameId?.toUpperCase()}
                  </Text>
                </Box>
                <Text textStyle="clue" fontSize="xs" opacity={0.6}>
                  Tap to copy
                </Text>
              </VStack>
            </motion.div>

            {/* QR Code for Players */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <VStack spacing={3} width="100%">
                <Center>
                  <Box bg="white" p={2} borderRadius="md">
                    <Image
                      src={qrCodeUrl}
                      alt="QR Code"
                      width="140px"
                      height="140px"
                    />
                  </Box>
                </Center>
                <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                  Players scan to join
                </Text>

                <HStack width="100%" spacing={2} maxW="280px">
                  <Button
                    flex={1}
                    size="sm"
                    onClick={handleShare}
                    bg="jeopardy.lightBlue"
                    leftIcon={<FaShareAlt />}
                  >
                    <Text textStyle="clue" fontSize="sm">
                      Share
                    </Text>
                  </Button>
                  <Button
                    flex={1}
                    size="sm"
                    onClick={copyJoinLink}
                    bg="jeopardy.lightBlue"
                  >
                    <Text textStyle="clue" fontSize="sm">
                      Copy Link
                    </Text>
                  </Button>
                </HStack>
              </VStack>
            </motion.div>

            {/* Players joined indicator */}
            {gameState.players.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                style={{ width: "100%", maxWidth: "280px" }}
              >
                <VStack spacing={2} width="100%">
                  <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                    {gameState.players.length} player
                    {gameState.players.length !== 1 ? "s" : ""} joined
                  </Text>
                  <Wrap justify="center" spacing={2}>
                    {gameState.players.map((player) => {
                      const audioProgress = gameState.playerAudioProgress?.[player.name];
                      const isGenerating = audioProgress?.status === 'generating';
                      const isComplete = audioProgress?.status === 'complete';

                      return (
                        <WrapItem key={player.name}>
                          <HStack
                            bg="rgba(255,255,255,0.1)"
                            px={3}
                            py={1}
                            borderRadius="md"
                            spacing={2}
                          >
                            <Text textStyle="clue" fontSize="sm">
                              {player.name}
                            </Text>
                            {isGenerating && (
                              <CircularProgress
                                value={(audioProgress.current / audioProgress.total) * 100}
                                size="16px"
                                color="yellow.400"
                                trackColor="whiteAlpha.300"
                              />
                            )}
                            {isComplete && (
                              <Text fontSize="sm" color="green.400">✓</Text>
                            )}
                          </HStack>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </VStack>
              </motion.div>
            )}

            {/* Divider */}
            <Box width="100%" maxW="280px" py={2}>
              <Divider borderColor="whiteAlpha.300" />
            </Box>

            {/* Join as Player */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              style={{ width: "100%", maxWidth: "280px" }}
            >
              <VStack spacing={4} width="100%">
                <Text textStyle="clue" fontSize="md">
                  Join as Player
                </Text>

                <FormControl>
                  <Input
                    value={hostName}
                    onChange={(e) => setHostName(e.target.value)}
                    placeholder="Your name"
                    bg="white"
                    color="black"
                    maxLength={10}
                    textAlign="center"
                    _placeholder={{ color: "gray.500" }}
                  />
                </FormControl>

                <Button
                  width="100%"
                  size="lg"
                  onClick={handleJoinAsPlayer}
                  isDisabled={!hostName.trim()}
                  isLoading={isJoining}
                  bg="jeopardy.gold"
                  color="black"
                  leftIcon={<FaPlay />}
                  _hover={{ bg: "yellow.400" }}
                  _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <Text textStyle="clue">Join Game</Text>
                </Button>

                <Text textStyle="clue" fontSize="xs" opacity={0.5}>
                  or
                </Text>

                <Button
                  width="100%"
                  size="md"
                  onClick={() => {
                    setIsHost(true);
                    setHostOnly(true);
                    setHostPhase("playing");
                  }}
                  bg="jeopardy.lightBlue"
                  color="white"
                  leftIcon={<FaCog />}
                  _hover={{ bg: "blue.500" }}
                >
                  <Text textStyle="clue" fontSize="sm">Control Game Only</Text>
                </Button>
                <Text textStyle="clue" fontSize="xs" opacity={0.5} textAlign="center">
                  Manage the game without playing
                </Text>
              </VStack>
            </motion.div>
          </VStack>
        </Center>
      </Flex>
    );
  }

  // Joining Phase
  if (hostPhase === "joining" || isJoining) {
    return (
      <Center height="100%" width="100%" background="jeopardy.blue">
        <VStack spacing={4}>
          <Spinner size="xl" color="white" />
          <Text textStyle="clue">Joining game...</Text>
        </VStack>
      </Center>
    );
  }

  // Playing Phase - Share Modal
  return (
    <Center height="100%" width="100%" background="jeopardy.blue">
      <Modal isOpen={isShareOpen} onClose={onShareClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="jeopardy.blue" mx={4}>
          <ModalHeader>
            <Text textStyle="clue" textAlign="center">
              Invite Players
            </Text>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Box
                bg="white"
                p={3}
                borderRadius="lg"
                cursor="pointer"
                onClick={copyGameCode}
              >
                <Text
                  fontSize="3xl"
                  fontWeight="bold"
                  color="jeopardy.blue"
                  textAlign="center"
                  letterSpacing="0.2em"
                >
                  {urlGameId?.toUpperCase()}
                </Text>
              </Box>
              <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                Tap code to copy
              </Text>

              <Center>
                <Image src={qrCodeUrl} alt="QR Code" borderRadius="md" />
              </Center>
              <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                Scan to join
              </Text>

              <Button
                width="100%"
                onClick={copyJoinLink}
                bg="jeopardy.lightBlue"
                leftIcon={<FaShareAlt />}
              >
                <Text textStyle="clue">Copy Join Link</Text>
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* TV Display Options Modal (for iOS) */}
      <Modal isOpen={isTvOpen} onClose={onTvClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="jeopardy.blue" mx={4}>
          <ModalHeader>
            <Text textStyle="clue" textAlign="center">
              Display on TV
            </Text>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              {/* Chromecast Option - Opens CastHelper via custom URL scheme */}
              <VStack spacing={3} width="100%">
                <Text textStyle="clue" fontSize="sm" textAlign="center">
                  📺 Have a Chromecast?
                </Text>
                <Button
                  as="a"
                  href={`casthelper://cast?code=${urlGameId}&app=82ADCE54&name=Jeoparty&receiver=${encodeURIComponent(castReceiverUrl)}&return=${encodeURIComponent(playerJoinUrl)}`}
                  width="100%"
                  size="lg"
                  bg="jeoparty.gold"
                  color="black"
                  leftIcon={<FaChromecast />}
                  _hover={{ bg: "yellow.400", textDecoration: "none" }}
                >
                  <Text fontWeight="bold">Cast to Chromecast</Text>
                </Button>
                <Text textStyle="clue" fontSize="xs" opacity={0.6}>
                  Opens Cast Helper app
                </Text>
              </VStack>

              <Divider borderColor="whiteAlpha.300" />

              {/* Manual URL Option */}
              <Text
                textStyle="clue"
                fontSize="sm"
                textAlign="center"
                opacity={0.8}
              >
                Or open this URL on any screen:
              </Text>

              <Box
                bg="white"
                p={3}
                borderRadius="lg"
                cursor="pointer"
                onClick={() => {
                  navigator.clipboard.writeText(castReceiverUrl);
                  toast({
                    title: "URL Copied!",
                    status: "success",
                    duration: 2000,
                  });
                }}
                width="100%"
              >
                <Text
                  fontSize="sm"
                  color="jeopardy.blue"
                  textAlign="center"
                  wordBreak="break-all"
                >
                  {castReceiverUrl}
                </Text>
              </Box>
              <Text textStyle="clue" fontSize="xs" opacity={0.6}>
                Tap to copy
              </Text>

              <Center>
                <Box bg="white" p={2} borderRadius="md">
                  <Image
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(
                      castReceiverUrl
                    )}`}
                    alt="TV QR Code"
                  />
                </Box>
              </Center>

              <Text
                textStyle="clue"
                fontSize="xs"
                textAlign="center"
                opacity={0.5}
              >
                Works on Smart TVs, laptops connected via HDMI, etc.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Player Management Modal */}
      <PlayerManagement isOpen={isPlayersOpen} onClose={onPlayersClose} />

      {/* Host bar component - shared between Player and HostOnlyControls */}
      {isHostOnly ? (
        <HostOnlyControls
          hostBar={
            <Flex
              width="100%"
              py="8px"
              px="12px"
              alignItems="center"
              justifyContent="space-between"
              bg="rgba(0,0,0,0.2)"
            >
              <Flex alignItems="center" gap={2}>
                <Box
                  bg="jeopardy.gold"
                  px={3}
                  py={1}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={copyGameCode}
                >
                  <Text
                    fontWeight="bold"
                    color="black"
                    fontSize="lg"
                    letterSpacing="0.1em"
                  >
                    {urlGameId?.toUpperCase()}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Share game"
                  icon={<FaQrcode />}
                  size="sm"
                  onClick={handleShare}
                  bg="jeopardy.lightBlue"
                />
                <IconButton
                  aria-label="Manage players"
                  icon={<FaUsers />}
                  size="sm"
                  onClick={onPlayersOpen}
                  bg="jeopardy.lightBlue"
                />
                {/* Skip to Double Jeopardy button - only show during Jeopardy round */}
                {gameState?.currentRound === Rounds.Jeopardy &&
                 gameState?.currentState === JeopardyStates.CategorySelection && (
                  <IconButton
                    aria-label="Skip to Double Jeopardy"
                    icon={<FaForward />}
                    size="sm"
                    onClick={() => {
                      sendEvent({ type: EventTypes.SKIP_TO_DOUBLE_JEOPARDY });
                      toast({
                        title: "Skipping to Double Jeopardy",
                        status: "info",
                        duration: 2000,
                      });
                    }}
                    bg="orange.500"
                    _hover={{ bg: "orange.400" }}
                    title="Skip to Double Jeopardy"
                  />
                )}
                {/* Skip to Final Jeopardy button - show during Jeopardy or Double Jeopardy rounds */}
                {(gameState?.currentRound === Rounds.Jeopardy || gameState?.currentRound === Rounds.DoubleJeopardy) &&
                 gameState?.currentState === JeopardyStates.CategorySelection && (
                  <IconButton
                    aria-label="Skip to Final Jeopardy"
                    icon={<FaFastForward />}
                    size="sm"
                    onClick={() => {
                      sendEvent({ type: EventTypes.SKIP_TO_FINAL_JEOPARDY });
                      toast({
                        title: "Skipping to Final Jeopardy",
                        status: "info",
                        duration: 2000,
                      });
                    }}
                    bg="red.500"
                    _hover={{ bg: "red.400" }}
                    title="Skip to Final Jeopardy"
                  />
                )}
              </Flex>

              <Flex alignItems="center" gap={2}>
                {/* Cast Button - iOS uses Universal Link, others use Cast SDK */}
                {isIOS ? (
                  <IconButton
                    as="a"
                    href={`casthelper://cast?code=${urlGameId}&app=82ADCE54&name=Jeoparty&receiver=${encodeURIComponent(castReceiverUrl)}&return=${encodeURIComponent(playerJoinUrl)}`}
                    aria-label="Cast to TV"
                    icon={<FaChromecast />}
                    size="sm"
                    bg="jeopardy.lightBlue"
                    _hover={{ bg: "jeoparty.gold" }}
                  />
                ) : isAvailable ? (
                  <IconButton
                    aria-label={
                      isConnected ? "Disconnect Chromecast" : "Cast to TV"
                    }
                    icon={isConnected ? <FaTv /> : <FaChromecast />}
                    size="sm"
                    onClick={handleCastClick}
                    bg={isConnected ? "green.500" : "jeopardy.lightBlue"}
                    _hover={{
                      bg: isConnected ? "green.400" : "jeoparty.gold",
                    }}
                  />
                ) : null}

                {/* Open Game Board in Browser */}
                <IconButton
                  aria-label="Open game board"
                  icon={<FaTv />}
                  size="sm"
                  onClick={() => window.open(castReceiverUrl, "_blank")}
                  bg="jeopardy.lightBlue"
                  title="Open game board in new tab"
                />
              </Flex>
            </Flex>
          }
        />
      ) : (
        /* Use Player component for the game UI with host bar */
        <Player
          isHost
          hostBar={(isLandscape) =>
            !isLandscape ? (
              <Flex
                width="100%"
                py="8px"
                px="12px"
                alignItems="center"
                justifyContent="space-between"
                bg="rgba(0,0,0,0.2)"
              >
                <Flex alignItems="center" gap={2}>
                  <Box
                    bg="jeopardy.gold"
                    px={3}
                    py={1}
                    borderRadius="md"
                    cursor="pointer"
                    onClick={copyGameCode}
                  >
                    <Text
                      fontWeight="bold"
                      color="black"
                      fontSize="lg"
                      letterSpacing="0.1em"
                    >
                      {urlGameId?.toUpperCase()}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Share game"
                    icon={<FaQrcode />}
                    size="sm"
                    onClick={handleShare}
                    bg="jeopardy.lightBlue"
                  />
                  <IconButton
                    aria-label="Manage players"
                    icon={<FaUsers />}
                    size="sm"
                    onClick={onPlayersOpen}
                    bg="jeopardy.lightBlue"
                  />
                  {/* Skip to Double Jeopardy button - only show during Jeopardy round */}
                  {gameState?.currentRound === Rounds.Jeopardy &&
                   gameState?.currentState === JeopardyStates.CategorySelection && (
                    <IconButton
                      aria-label="Skip to Double Jeopardy"
                      icon={<FaForward />}
                      size="sm"
                      onClick={() => {
                        sendEvent({ type: EventTypes.SKIP_TO_DOUBLE_JEOPARDY });
                        toast({
                          title: "Skipping to Double Jeopardy",
                          status: "info",
                          duration: 2000,
                        });
                      }}
                      bg="orange.500"
                      _hover={{ bg: "orange.400" }}
                      title="Skip to Double Jeopardy"
                    />
                  )}
                  {/* Skip to Final Jeopardy button - show during Jeopardy or Double Jeopardy rounds */}
                  {(gameState?.currentRound === Rounds.Jeopardy || gameState?.currentRound === Rounds.DoubleJeopardy) &&
                   gameState?.currentState === JeopardyStates.CategorySelection && (
                    <IconButton
                      aria-label="Skip to Final Jeopardy"
                      icon={<FaFastForward />}
                      size="sm"
                      onClick={() => {
                        sendEvent({ type: EventTypes.SKIP_TO_FINAL_JEOPARDY });
                        toast({
                          title: "Skipping to Final Jeopardy",
                          status: "info",
                          duration: 2000,
                        });
                      }}
                      bg="red.500"
                      _hover={{ bg: "red.400" }}
                      title="Skip to Final Jeopardy"
                    />
                  )}
                </Flex>

                <Flex alignItems="center" gap={2}>
                  {/* Cast Button - iOS uses Universal Link, others use Cast SDK */}
                  {isIOS ? (
                    <IconButton
                      as="a"
                      href={`casthelper://cast?code=${urlGameId}&app=82ADCE54&name=Jeoparty&receiver=${encodeURIComponent(castReceiverUrl)}&return=${encodeURIComponent(playerJoinUrl)}`}
                      aria-label="Cast to TV"
                      icon={<FaChromecast />}
                      size="sm"
                      bg="jeopardy.lightBlue"
                      _hover={{ bg: "jeoparty.gold" }}
                    />
                  ) : isAvailable ? (
                    <IconButton
                      aria-label={
                        isConnected ? "Disconnect Chromecast" : "Cast to TV"
                      }
                      icon={isConnected ? <FaTv /> : <FaChromecast />}
                      size="sm"
                      onClick={handleCastClick}
                      bg={isConnected ? "green.500" : "jeopardy.lightBlue"}
                      _hover={{
                        bg: isConnected ? "green.400" : "jeoparty.gold",
                      }}
                    />
                  ) : null}

                  {/* Open Game Board in Browser */}
                  <IconButton
                    aria-label="Open game board"
                    icon={<FaTv />}
                    size="sm"
                    onClick={() => window.open(castReceiverUrl, "_blank")}
                    bg="jeoparty.lightBlue"
                    title="Open game board in new tab"
                  />
                </Flex>
              </Flex>
            ) : null
          }
        />
      )}
    </Center>
  );
};
